import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Blog.css';

interface BlogPost {
  id:            string;
  title:         string;
  slug:          string;
  status:        string;
  category:      string;
  services:      string[];
  location:      string;
  clientType:    string;
  summary:       string;
  coverImageUrl: string;
  publishDate:   string | null;
  author:        string;
  featured:      boolean;
  tags:          string[];
}

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:5000';

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const Blog: React.FC = () => {
  const [posts, setPosts]     = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/blog/posts`)
      .then(r => {
        if (!r.ok) throw new Error(`API returned ${r.status}`);
        return r.json() as Promise<BlogPost[]>;
      })
      .then(data => {
        setPosts(data);
        setLoading(false);
      })
      .catch(err => {
        setError('Unable to load blog posts right now. Please try again shortly.');
        setLoading(false);
        console.error('Blog fetch error:', err);
      });
  }, []);

  const featured  = posts.filter(p => p.featured);
  const remaining = posts.filter(p => !p.featured);

  return (
    <div className="blog-page">

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section className="page-hero section--dark">
        <div className="container-xl">
          <span className="section-label">Field Notes</span>
          <h1 className="section-title section-title--white">Project Stories &amp; Insights</h1>
          <p className="section-sub section-sub--white" style={{ marginTop: '1rem' }}>
            Real jobs from Greater Houston — what we found, what we fixed, and what it means
            for your property's compliance and curb appeal.
          </p>
        </div>
      </section>
      <div className="red-bar" />

      {/* ── CONTENT ──────────────────────────────────────────────── */}
      <section className="section">
        <div className="container-xl">

          {loading && (
            <div className="blog-state blog-state--loading">
              <div className="blog-spinner" />
              <p>Loading posts…</p>
            </div>
          )}

          {error && !loading && (
            <div className="blog-state blog-state--error">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && posts.length === 0 && (
            <div className="blog-state">
              <p>No published posts yet — check back soon.</p>
            </div>
          )}

          {!loading && !error && posts.length > 0 && (
            <>
              {/* Featured posts */}
              {featured.length > 0 && (
                <div className="blog-featured-grid">
                  {featured.map(post => (
                    <BlogCard key={post.id} post={post} featured />
                  ))}
                </div>
              )}

              {/* Remaining posts */}
              {remaining.length > 0 && (
                <div className="blog-card-grid">
                  {remaining.map(post => (
                    <BlogCard key={post.id} post={post} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      <section className="section--dark" style={{ padding: 'clamp(3rem,6vw,5rem) 0', borderTop: '3px solid var(--red)' }}>
        <div className="container-xl">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-4">
            <div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 'clamp(1.6rem,3vw,2.5rem)', textTransform: 'uppercase', color: 'var(--white)', marginBottom: '0.5rem' }}>
                Your Property Could Be Next.
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 15, fontWeight: 300, margin: 0 }}>
                Free assessment. Quote within 24 hours. No obligation.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-3">
              <Link to="/contact" className="btn btn-brand-primary">Get a Free Quote</Link>
              <a href="tel:3464194499" className="btn btn-brand-outline-white">(346) 419-4499</a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

// ── Blog Card ─────────────────────────────────────────────────────────
interface CardProps { post: BlogPost; featured?: boolean; }

const BlogCard: React.FC<CardProps> = ({ post, featured = false }) => {
  const hasImage = post.coverImageUrl && post.coverImageUrl.trim() !== '';

  return (
    <article className={`blog-card${featured ? ' blog-card--featured' : ''}`}>
      {/* Cover image */}
      <div className="blog-card__img">
        {hasImage ? (
          <img src={post.coverImageUrl} alt={post.title} loading="lazy" />
        ) : (
          <div className="blog-card__img-placeholder">
            <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21,15 16,10 5,21" />
            </svg>
          </div>
        )}
        {/* Category badge */}
        {post.category && (
          <span className="blog-card__category">{post.category}</span>
        )}
        {/* Featured badge */}
        {featured && (
          <span className="blog-card__featured-badge">Featured</span>
        )}
      </div>

      {/* Body */}
      <div className="blog-card__body">
        {/* Meta row */}
        <div className="blog-card__meta">
          {post.publishDate && <span>{formatDate(post.publishDate)}</span>}
          {post.location    && <span>📍 {post.location}</span>}
          {post.clientType  && <span className="tag tag--navy">{post.clientType}</span>}
        </div>

        {/* Title */}
        <h2 className="blog-card__title">{post.title}</h2>

        {/* Summary */}
        {post.summary && <p className="blog-card__summary">{post.summary}</p>}

        {/* Services chips */}
        {post.services.length > 0 && (
          <div className="blog-card__chips">
            {post.services.map(s => (
              <span className="svc-chip" key={s}>{s}</span>
            ))}
          </div>
        )}

        {/* Tags */}
        {post.tags.length > 0 && (
          <div className="blog-card__tags">
            {post.tags.map(t => (
              <span className="blog-tag" key={t}>#{t}</span>
            ))}
          </div>
        )}

        {/* Read more — links to /blog/:slug when you build individual post pages */}
        <Link to={`/blog/${post.slug}`} className="btn btn-brand-navy blog-card__cta">
          Read Full Story →
        </Link>
      </div>
    </article>
  );
};

export default Blog;
